import { EActivityType, ETaskStatus } from '@constants';
import {
  CONTENT_CHECKS,
  EContentChecks,
  EPerformanceChecks,
  ETechnicalChecks,
  IContentChecks,
  IPerformanceChecks,
  ITaskValidation,
  ITechnicalChecks,
  PERFORMANCE_CHECKS,
  TECHNICAL_CHECKS
} from '@sdk/contracts';
import { Checkbox, Drawer, Flex, Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import TextArea from 'antd/lib/input/TextArea';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputTitleWrapper, Wrapper } from './ChangeStatusForm.styles';
import { DrawerHeader } from './DrawerHeader';
import { TaskChecks } from './TaskChecks';
import { TaskPreview } from './TaskPreview';

import { RULES } from '@/constants';
import { useChangeStatusForm } from '@/hooks';
import { useMutateActivity } from '@/store';

const DRAWER_WIDTH = 500;

const sectionMap = (
  checksEnum: EContentChecks[] | EPerformanceChecks[] | ETechnicalChecks[],
  isChecking: boolean
) => {
  let map = {};
  for (const check of checksEnum) {
    map = { ...map, [check]: isChecking };
  }
  return map;
};

export const ChangeStatusForm = () => {
  const { t } = useTranslation('CourseTranslations');
  const { task, setTask } = useChangeStatusForm();
  const { validateTask } = useMutateActivity();
  const [validateStatus, setValidateStatus] = useState<ETaskStatus>();
  const [form] = useForm<ITaskValidation>();
  const checks = Form.useWatch('checks', form);

  useEffect(() => {
    if (task?.validation?.checks)
      return form.setFieldsValue({
        checks: task.validation.checks
      });

    form.setFieldsValue({
      checks: {
        technical: sectionMap(TECHNICAL_CHECKS, false),
        content: sectionMap(CONTENT_CHECKS, false),
        performance: sectionMap(PERFORMANCE_CHECKS, false)
      }
    });
  }, [task]);

  const section = useMemo(() => {
    return task?.course?.sections.find(s =>
      s.activities.some(activity => activity.task?._id === task?._id)
    );
  }, [task]);

  const activity = useMemo(() => {
    return section?.activities.find(a => a.task?._id === task?._id);
  }, [section, task]);

  const isVideo = useMemo(() => activity?.type === EActivityType.VIDEO, [activity]);

  const isAllChecked = useMemo(() => {
    if (!isVideo) return true;
    if (!checks) return false;
    return Object.values(checks).every(s => Object.values(s).every(check => check));
  }, [checks, isVideo]);

  const handleCancel = () => {
    setValidateStatus(undefined);
    setTask();
    form.resetFields();
  };

  const handleCheckAll = () => {
    const checked = !isAllChecked;
    form.setFieldsValue({
      checks: {
        technical: sectionMap(TECHNICAL_CHECKS, checked) as ITechnicalChecks,
        content: sectionMap(CONTENT_CHECKS, checked) as IContentChecks,
        performance: sectionMap(PERFORMANCE_CHECKS, checked) as IPerformanceChecks
      }
    });
  };

  const handleOnFinish = (validation: ITaskValidation) => {
    if (!task || !validateStatus || !activity?._id) return;
    validateTask(
      {
        validation,
        taskId: task._id,
        status: validateStatus,
        activityId: activity._id
      },
      {
        onSuccess: () => {
          handleCancel();
        }
      }
    );
  };

  const onFinish = (status: ETaskStatus) => () => {
    setValidateStatus(status);
    form.submit();
  };

  return (
    <Drawer
      width={DRAWER_WIDTH}
      open={!!task}
      onClose={handleCancel}
      title={<DrawerHeader onFinish={onFinish} isAllChecked={isAllChecked} />}>
      <Wrapper>
        <TaskPreview activity={activity} />
        {isVideo && (
          <>
            <div>{t('CHECK_CORRECT')}</div>
            <Checkbox checked={isAllChecked} onChange={handleCheckAll}>
              {t('SELECT_ALL')}
            </Checkbox>
          </>
        )}
        <Form layout="vertical" form={form} onFinish={handleOnFinish}>
          {isVideo && <TaskChecks />}
          <Form.Item name="comments" rules={[RULES.REQUIRED]}>
            <Flex vertical align="flex-start" gap={4} style={{ marginTop: 24 }}>
              <InputTitleWrapper>{t('ADD_COMMENTS')}</InputTitleWrapper>
              <TextArea rows={4} placeholder={t('ADD_COMMENTS')} />
            </Flex>
          </Form.Item>
        </Form>
      </Wrapper>
    </Drawer>
  );
};
